import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Skeleton,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import PageLayout from '../components/layout/PageLayout';
import { useDispatch, useSelector } from 'react-redux';
import { getLeadsComments } from '../store/slices/dashboardSlice';

const HEADER_BG = '#c8e6c9';

export default function LeadsReport() {
  const dispatch = useDispatch();
  const response = useSelector((s) => s.dashboard.leadsComments);
  const loading = useSelector((s) => s.dashboard.loading);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);

  const list = useMemo(() => {
    const raw = response?.data;
    return Array.isArray(raw) ? raw : [];
  }, [response]);

  const searchLower = (search || '').toLowerCase().trim();
  const filtered = useMemo(() => {
    if (!searchLower) return list;
    return list.filter((row) => {
      const dba = String(row.dba_name ?? '').toLowerCase();
      const agent = String(row.agent_name ?? '').toLowerCase();
      const number = String(row.number ?? '').toLowerCase();
      const date = String(row.date ?? '').toLowerCase();
      const leadId = String(row.lead_id ?? '').toLowerCase();
      return (
        dba.includes(searchLower) ||
        agent.includes(searchLower) ||
        number.includes(searchLower) ||
        date.includes(searchLower) ||
        leadId.includes(searchLower)
      );
    });
  }, [list, searchLower]);

  const from = page * rowsPerPage;
  const paginated = useMemo(
    () => filtered.slice(from, from + rowsPerPage),
    [filtered, from, rowsPerPage]
  );

  const fetchData = useCallback(() => {
    dispatch(getLeadsComments({}));
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, []);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    setExpandedRow(null);
  };
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
    setExpandedRow(null);
  };

  const toggleExpand = (leadId) => {
    setExpandedRow((prev) => (prev === leadId ? null : leadId));
  };

  const columnCount = 6;

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          Leads Comments
        </Typography>

        <Card>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Leads comments
              </Typography>
              <TextField
                size="small"
                placeholder="Search by lead ID, date, DBA name, number, or agent..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 320 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER_BG, width: 48 }} />
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER_BG }}>DBA Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER_BG }}>Number</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER_BG }}>Agent</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER_BG }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER_BG }} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={columnCount}>
                          <Skeleton height={36} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columnCount} sx={{ textAlign: 'center', py: 4 }}>
                        {list.length === 0
                          ? 'No leads data available.'
                          : 'No rows match your search.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((row, i) => {
                      const leadId = row.lead_id ?? i;
                      const followups = row.followups ?? [];
                      const isExpanded = expandedRow === leadId;
                      const hasFollowUps = followups.length > 0;

                      return (
                        <React.Fragment key={leadId}>
                          <TableRow
                            sx={{
                              '& > *': { borderBottom: isExpanded ? 'none' : undefined },
                              bgcolor: isExpanded ? 'action.hover' : undefined,
                            }}
                          >
                            <TableCell sx={{ width: 48, py: 0.75 }}>
                              <IconButton
                                size="small"
                                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                onClick={() => toggleExpand(leadId)}
                                sx={{ p: 0.5 }}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {row.dba_name ?? '—'}
                            </TableCell>
                            <TableCell>{row.number ?? '—'}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.agent_name ?? '—'}
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: 1.5 }}
                              />
                            </TableCell>
                            <TableCell>{row.date ?? '—'}</TableCell>
                            <TableCell align="right" sx={{ width: 48 }}>
                              <IconButton size="small" aria-label="More options">
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell
                                colSpan={columnCount}
                                sx={{
                                  py: 0,
                                  px: 0,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'grey.50',
                                  verticalAlign: 'top',
                                }}
                              >
                                <Box sx={{ pl: 6, pr: 2, pt: 2, pb: 2 }}>
                                  {!hasFollowUps ? (
                                    <Typography variant="body2" color="text.secondary">
                                      No follow-ups available.
                                    </Typography>
                                  ) : (
                                    <>
                                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ fontWeight: 600 }}>Stages</TableCell>
                                              <TableCell sx={{ fontWeight: 600 }}>Remarks</TableCell>
                                              <TableCell sx={{ fontWeight: 600 }}>Follow-up Date</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {followups.map((fu, idx) => (
                                              <TableRow key={idx}>
                                                <TableCell>
                                                  <Chip
                                                    label={fu.follow_up_label ?? '—'}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ borderRadius: 1.5 }}
                                                  />
                                                </TableCell>
                                                <TableCell>
                                                  {fu.remarks ?? 'No available remarks'}
                                                </TableCell>
                                                <TableCell>{fu.follow_up_date ?? '—'}</TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && filtered.length > 0 && (
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
}
